import { useEffect, useRef, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { downloadBlob } from "@/components/ToolLayout";
import { minifyCss, minifyHtml, minifyJs, byteLength, gzipSize, type MinifyResult } from "./minifyUtils";
import { copyText } from "@/lib/copyText";

const TOOL_INFO_CONTENT = {
  what: `Minifies CSS, JavaScript and HTML with real parsers (Terser for JavaScript, csso for CSS, html-minifier-terser for HTML including its inline style and script blocks) so the output behaves exactly like your original code, and shows the original, minified and gzip sizes.`,
  how: `Paste or upload your CSS, JavaScript or HTML and the minified version is generated automatically. Syntax errors are shown with the line and column.`,
  tip: `JavaScript is minified with variable renaming and dead-code removal, so keep your original file — minified code is hard to read. If your code uses top-level names from other scripts, check they still match.`,
};

const EXAMPLES = {
  css: "/* example */\n.button {\n  color: red;\n  padding: 10px;\n}",
  js: "// example\nfunction greet(name) {\n  return `Hello, ${name}!`;\n}",
  html: "<!-- example -->\n<div class=\"card\">\n  <h1>  Hello   world  </h1>\n  <p>Some text</p>\n</div>",
} as const;

export default function CssJsMinifier() {
  const [mode, setMode] = useState<"css" | "js" | "html">("css");
  const [input, setInput] = useState<string>(EXAMPLES.css);

  const [result, setResult] = useState<MinifyResult>({ ok: true, output: "" });
  const [sizes, setSizes] = useState({ original: 0, minified: 0, gzip: 0 });
  const runId = useRef(0);

  useEffect(() => {
    const id = ++runId.current;
    const t = setTimeout(async () => {
      const r = mode === "css" ? await minifyCss(input) : mode === "html" ? await minifyHtml(input) : await minifyJs(input);
      const gz = r.ok ? await gzipSize(r.output) : 0;
      if (id !== runId.current) return;
      setResult(r);
      setSizes({ original: byteLength(input), minified: byteLength(r.output), gzip: gz });
    }, 300);
    return () => clearTimeout(t);
  }, [input, mode]);

  // Switching language swaps the sample text, but never throws away something the user typed.
  const switchMode = (m: "css" | "js" | "html") => {
    if (Object.values(EXAMPLES).includes(input as never) || !input.trim()) setInput(EXAMPLES[m]);
    setMode(m);
  };

  const output = result.output;
  const savings = sizes.original > 0 && result.ok ? Math.round((1 - sizes.minified / sizes.original) * 100) : 0;

  const copy = () => {
    void copyText(output, "Copied to clipboard");
  };

  const onFile = async (f: File | undefined) => {
    if (!f) return;
    setMode(/\.m?js$/i.test(f.name) ? "js" : /\.html?$/i.test(f.name) ? "html" : "css");
    setInput(await f.text());
  };

  const download = () => {
    downloadBlob(new Blob([output], { type: "text/plain" }), mode === "css" ? "styles.min.css" : mode === "html" ? "index.min.html" : "script.min.js");
  };

  const { blocked } = useLogToolUsage(true, 3000, { ownActions: true });


  return (
    <ToolContainer>
      <div className="flex items-center gap-3 mb-4">
        <Button
          size="sm"
          variant={mode === "css" ? "default" : "outline"}
          onClick={() => switchMode("css")}
        >
          CSS
        </Button>
        <Button
          size="sm"
          variant={mode === "js" ? "default" : "outline"}
          onClick={() => switchMode("js")}
        >
          JavaScript
        </Button>
        <Button
          size="sm"
          variant={mode === "html" ? "default" : "outline"}
          onClick={() => switchMode("html")}
        >
          HTML
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        <label className="underline cursor-pointer">
          Upload a .css, .js or .html file
          <input type="file" accept=".css,.js,.mjs,.html,.htm,text/css,text/javascript,text/html" className="hidden" onChange={e => onFile(e.target.files?.[0])} />
        </label>
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="mb-2 block">Input</Label>
          <Textarea aria-label="Input"
            value={input}
            onChange={e => setInput(e.target.value)}
            className="min-h-[250px] font-mono text-sm"
          />
        </div>
        <div>
          <Label className="mb-2 block">
            Minified {savings > 0 && `(${savings}% smaller)`}
          </Label>
          <Textarea
            readOnly
            value={blocked ? "— blocked at today's usage limit —" : output}
            className="min-h-[250px] font-mono text-sm bg-muted/30"
          />
        </div>
      </div>
      {!result.ok && result.error && (
        <p className="mt-3 text-sm text-destructive" role="alert">
          {mode === "css" ? "CSS" : mode === "html" ? "HTML" : "JavaScript"} error
          {result.error.line ? ` at line ${result.error.line}${result.error.column !== undefined ? `, column ${result.error.column}` : ""}` : ""}
          : {result.error.message}
        </p>
      )}
      {result.ok && sizes.original > 0 && (
        <p className="mt-3 text-sm text-muted-foreground">
          Original {sizes.original.toLocaleString()} B → minified {sizes.minified.toLocaleString()} B
          {sizes.gzip > 0 && ` → gzip ${sizes.gzip.toLocaleString()} B`}
          {sizes.gzip > sizes.minified && sizes.minified > 0 && " (gzip adds a fixed header, so it can be larger than the text on very small input)"}
        </p>
      )}
      <div className="flex gap-2 mt-4">
        <Button onClick={copy} disabled={blocked || !result.ok}>
          Copy Output
        </Button>
        <Button variant="outline" onClick={download} disabled={blocked || !result.ok || !output}>
          Download
        </Button>
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
